/** @file This component displays user notifications. */
import moment from 'moment';
import * as tsx from 'vue-tsx-support';
import { Settings } from '@codegrade/icons';
import * as button from '@/components/Button';
import { Card } from '@/components/Card';
import { Icon } from '@/components/Icon';
import * as infSearch from '@/components/InfiniteSearchScrollable';
import NotificationSettings from '@/components/NotificationSettings';
import { NotificationStore } from '@/store';
import * as utils from '@/utils';
import { AccessibleStatus } from '../VisuallyHiddenLiveRegions';
import InnerUserNotifications from './InnerUserNotifications';
import { NotificationSearcher } from './notifications-searcher';
import './style.less';
const NotificationsScrollable = infSearch.create('notifications', {
    debounceTime: moment.duration(0, 'ms'),
});
export default tsx.componentFactoryOf().create({
    name: 'user-notifications',
    /** @returns The data for the component. */
    data() {
        return {
            showSettings: false,
            checkedNotificationIds: [],
            showRead: false,
            notificationsTableId: `notifications-list-${utils.getUniqueId()}`,
        };
    },
    computed: {
        /** @returns An instance of the NotificationSearcher based on the current `showRead` state. */
        searcher() {
            return new NotificationSearcher(this.showRead);
        },
        /** @returns True if there are unread notifications. */
        hasUnreadNotifications() {
            return NotificationStore.getHasUnreadNotifications();
        },
        /** @returns The currently displayed notifications from the store. */
        notifications() {
            const result = NotificationStore.getNotifications()({ unreadOnly: !this.showRead });
            return result.data ?? [];
        },
        /** @returns Info on unread data. */
        unreadCounts() {
            return this.notifications.reduce((acc, n) => {
                if (!n.read) {
                    acc.unread++;
                    if (this.checkedNotificationIds.includes(n.id)) {
                        acc.selected++;
                    }
                }
                return acc;
            }, { unread: 0, selected: 0 });
        },
        /**
         * Determines the state of the "select all" checkbox.
         * Uses the filtered notifications from the searcher and will show a
         * check, dash or be empty depending on the state.
         *
         * @returns An object with `checked` and `indeterminate` booleans.
         */
        selectAllState() {
            const { unread, selected } = this.unreadCounts;
            // All unchecked (no check).
            if (unread === 0) {
                return { checked: false, indeterminate: false };
            }
            // Some checked (show dash).
            if (selected > 0 && selected < unread) {
                return { checked: false, indeterminate: true };
            }
            // All checked (show check).
            if (selected === unread) {
                return { checked: true, indeterminate: false };
            }
            return { checked: false, indeterminate: false };
        },
        /** @returns The node for the notification settings. */
        settingsNode() {
            return (<b-collapse visible={this.showSettings} class="grow-0">
                    <div class={[
                    'border-b',
                    'border-b-neutral-10',
                    'dark:border-b-neutral-70',
                    'notification-settings-wrapper',
                    '!p-4',
                ]}>
                        <NotificationSettings />
                    </div>
                </b-collapse>);
        },
        /** @returns The settings toggle button node in the card header. */
        settingsButtonNode() {
            return (<button.Button action={() => this.toggleViewSettings()} prominence={2} buttonClasses="min-h-8 min-w-8" semantic={this.showSettings ? 'actionable' : 'neutral'} aria-expanded={this.showSettings} aria-controls="notification-settings-panel" aria-label={`${this.showSettings ? 'Hide' : 'Show'} notification settings`}>
                    <Icon icon={Settings} aria-hidden="true"/>
                    Settings
                </button.Button>);
        },
        /** @returns The select all checkbox for notifications. */
        selectAllCheckboxNode() {
            return (<div class="flex items-center">
                    <input data-cy="notifications-select-all-checkbox" ref="selectAllCheckbox" class="size-4" type="checkbox" disabled={!this.hasUnreadNotifications} aria-label="Select all unread notifications" aria-controls={this.notificationsTableId} checked={this.selectAllState.checked} onInput={evt => this.handleSelectAll(evt)}/>
                </div>);
        },
        /** @returns The button node which will mark checked notifications as read. */
        markCheckedAsReadNode() {
            return utils.ifOrEmpty(this.checkedNotificationIds.length > 0, () => (<button.PromiseButton data-cy="notifications-mark-checked-btn" aria-label={`Mark ${this.checkedNotificationIds.length} selected notifications as read`} buttonClasses="mr-2" label="Mark as read" loadingAlertMessage="Marking notifications as read" successAlertMessage="Notifications marked as read" action={() => this.markAsRead(this.checkedNotificationIds)}/>));
        },
        /** @returns The `Show read notifications` node for the toolbar. */
        showReadCheckboxNode() {
            return (<label class="flex items-center ml-auto cursor-pointer gap-2 mb-0">
                    <span class="text-sm self-center">Show read notifications</span>
                    <input data-cy="notifications-show-read-checkbox" aria-controls={this.notificationsTableId} class="size-4 self-center" type="checkbox" checked={this.showRead} onChange={() => {
                    this.showRead = !this.showRead;
                }}/>
                </label>);
        },
        /** @returns The toolbar node for the table. */
        toolbarNode() {
            return (<div class={[
                    'flex',
                    'items-center',
                    'gap-4',
                    'min-h-12',
                    '!pl-7',
                    '!pr-5',
                    'border-b',
                    'border-x',
                    'bg-neutral-1 dark:bg-neutral-75',
                    'border-neutral-5 dark:border-neutral-80',
                ]} aria-label="Notification actions">
                    {this.selectAllCheckboxNode}
                    {this.markCheckedAsReadNode}
                    {this.showReadCheckboxNode}
                </div>);
        },
        /** @returns The accessible status info for screen readers. */
        statusNode() {
            const msg = [];
            if (this.checkedNotificationIds.length > 0) {
                msg.push(`${this.checkedNotificationIds.length} notification${this.checkedNotificationIds.length === 1 ? '' : 's'} selected. `);
            }
            if (this.showRead) {
                msg.push('Showing all notifications including read. ');
            }
            else {
                msg.push('Showing only unread notifications. ');
            }
            const count = this.notifications.length;
            const hasUnread = this.notifications.some(n => !n.read);
            if (count === 0) {
                msg.push('No notifications found. ');
            }
            else {
                msg.push(`${count} notification${count === 1 ? '' : 's'} loaded. `);
                if (!this.showRead && !hasUnread) {
                    msg.push('You are all caught up! ');
                }
            }
            return <AccessibleStatus for={this.notificationsTableId} content={msg}/>;
        },
    },
    methods: {
        /** @returns The result of marking all checked notifications as read. */
        async markAsRead(notificationIds) {
            const result = await NotificationStore.markManyAsRead({ notificationIds });
            return result.addCallbacks({
                onAfterSuccess: () => {
                    this.checkedNotificationIds = this.checkedNotificationIds.filter(id => !notificationIds.includes(id));
                },
            });
        },
        /** Toggles the notification setting. */
        toggleViewSettings() {
            this.showSettings = !this.showSettings;
        },
        /**
         * Toggles an individual user notification and updates the listing.
         *
         * @param notificationId The id of the notificaiton.
         * @param checked Whether it's checked or not.
         */
        toggleCheckbox(notificationId, checked) {
            if (checked) {
                if (!this.checkedNotificationIds.includes(notificationId)) {
                    this.checkedNotificationIds.push(notificationId);
                }
            }
            else {
                const index = this.checkedNotificationIds.indexOf(notificationId);
                if (index > -1) {
                    this.checkedNotificationIds.splice(index, 1);
                }
            }
        },
        /**
         * Handles the `select all` checkbox being clicked.
         *
         * @param evt Event when clicking the checkbox.
         */
        handleSelectAll(evt) {
            const unreadNotifications = this.notifications.filter(n => !n.read);
            if (evt.target.checked) {
                this.checkedNotificationIds = unreadNotifications.map(n => n.id);
            }
            else {
                this.checkedNotificationIds = [];
            }
        },
        /** @returns The scrollable notifications listing. */
        renderNotifications() {
            return (<NotificationsScrollable searcher={this.searcher} query="" scopedSlots={{
                    noItemsFound: () => (<div class="flex justify-center py-2 text-neutral-50 dark:text-neutral-30" role="status" aria-live="polite">
                                {this.showRead ? 'No notifications' : 'You are all caught up!'}
                            </div>),
                    body: ({ items, statusNode, loadMoreButton }) => (<div>
                                {statusNode}
                                <InnerUserNotifications notificationsTableId={this.notificationsTableId} checkedNotificationIds={this.checkedNotificationIds} notifications={items} showRead={this.showRead} onToggleCheckbox={payload => {
                            this.toggleCheckbox(payload.notificationId, payload.checked);
                        }} loadMoreNode={loadMoreButton}/>
                            </div>),
                }}/>);
        },
    },
    watch: {
        /** Updates the indeterminate property of the checkbox DOM element whenever the state changes. */
        selectAllState() {
            this.$nextTick(() => {
                const checkbox = this.$refs.selectAllCheckbox;
                if (checkbox != null) {
                    checkbox.indeterminate = this.selectAllState.indeterminate;
                }
            });
        },
        /**
         * Update the checked notifications on store change.
         *
         * @param updatedNotifications The changed notifications.
         */
        notifications(updatedNotifications) {
            const readNotificationIds = updatedNotifications.filter(n => n.read).map(n => n.id);
            this.checkedNotificationIds = this.checkedNotificationIds.filter(id => !readNotificationIds.includes(id));
        },
    },
    /** @returns A VNode of the rendered UserNotification. */
    render() {
        return (<Card header="Notifications" bodyClass={['flex', 'flex-col', 'flex-grow', 'p-0', 'm-0', 'min-h-0']}>
                <div slot="header" class="contents" aria-hidden={!this.showSettings}>
                    {this.settingsButtonNode}
                </div>

                <div class="user-notifications-card flex flex-col flex-grow" id="notification-settings-panel">
                    {this.statusNode}
                    {this.settingsNode}
                    {this.toolbarNode}
                    {this.renderNotifications()}
                </div>
            </Card>);
    },
});
